import styled, { css, keyframes } from "styled-components";
import { pxToRem, withAlpha } from "styles/utils";
import { ButtonSize, ButtonVariant, StyledButtonProps } from "./Button.types";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const primaryStyles = css`
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.brand};
  box-shadow: none;
  color: ${({ theme }) => theme.colors.white};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.brandDark};
  }

  &:active:not(:disabled) {
    background: ${({ theme }) => theme.colors.brandDeep};
  }
`;

const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  primary: primaryStyles,
  panelPrimary: primaryStyles,

  accent: css`
    border: ${pxToRem(1)} solid ${({ theme }) => theme.colors.brandMedium};
    background: ${({ theme }) => theme.colors.brandSoft};
    box-shadow: none;
    color: ${({ theme }) => theme.colors.brandDark};

    &:hover:not(:disabled) {
      border-color: ${({ theme }) => theme.colors.brand};
      background: ${({ theme }) => theme.colors.brandMedium};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.brandSoft};
    }
  `,

  secondary: css`
    border: ${pxToRem(1)} solid ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.white};
    box-shadow: none;
    color: ${({ theme }) => theme.colors.brand};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.brandSubtle};
      color: ${({ theme }) => theme.colors.brandDark};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.brandSoft};
    }
  `,

  ghost: css`
    border: ${pxToRem(1)} solid transparent;
    background: transparent;
    box-shadow: none;
    color: ${({ theme }) => theme.colors.brand};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.brandSubtle};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.brandSoft};
    }
  `,

  danger: css`
    background: ${({ theme }) => theme.colors.danger};
    box-shadow: none;
    color: ${({ theme }) => theme.colors.white};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.dangerPink};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.dangerAccent};
    }
  `,

  dangerSecondary: css`
    border: ${pxToRem(1)} solid ${({ theme }) => withAlpha(theme.colors.dangerText, 0.28)};
    background: transparent;
    box-shadow: none;
    color: ${({ theme }) => theme.colors.dangerText};

    &:hover:not(:disabled) {
      border-color: ${({ theme }) => withAlpha(theme.colors.dangerText, 0.4)};
      background: ${({ theme }) => withAlpha(theme.colors.dangerText, 0.07)};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => withAlpha(theme.colors.dangerText, 0.12)};
    }
  `,
};

const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
    padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  `,
  md: css`
    height: ${({ theme }) => pxToRem(theme.controlHeight.md)};
    padding: 0 ${({ theme }) => pxToRem(theme.spacing.lg)};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  `,
  lg: css`
    height: ${({ theme }) => pxToRem(theme.controlHeight.lg)};
    padding: 0 ${({ theme }) => pxToRem(theme.spacing.xl)};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  `,
};

export const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  width: 100%;
  min-width: ${pxToRem(100)};
  max-width: ${({ $fullWidth }) => ($fullWidth ? "none" : pxToRem(300))};
  border: none;
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease,
    opacity 0.18s ease;

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  &:disabled {
    cursor: not-allowed;
    box-shadow: none;
    opacity: 0.3;
    transform: none;
  }
`;

export const ButtonSpinner = styled.span`
  display: inline-block;
  width: ${pxToRem(14)};
  height: ${pxToRem(14)};
  flex: 0 0 auto;
  border: ${pxToRem(2)} solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.6;
  }
`;
